import React, { useState } from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { useCollectionStore } from '../lib/collectionStore';
import { log } from '../components/DebugLogger';
import { useProfileStore } from '../lib/supabase';

export type CardFinish = string;

export interface FinishOption {
  value: CardFinish;
  label: string;
  color: string;
  dotColor: string;
  textColor: string;
  description: string;
}

interface CollectionButtonProps {
  card: Card;
  compact?: boolean;
  defaultFinish?: CardFinish;
  availableFinishes?: CardFinish[] | null;
}

export const ALL_FINISH_OPTIONS: FinishOption[] = [
  { value: 'normal', label: 'Normal', color: 'border-yellow-500/30', dotColor: 'bg-yellow-500', textColor: 'text-yellow-500', description: 'Regular card' },
  { value: 'holofoil', label: 'Holofoil', color: 'border-purple-500/30', dotColor: 'bg-purple-500', textColor: 'text-purple-500', description: 'Traditional holo pattern' },
  { value: 'reverse_holofoil', label: 'Reverse Holofoil', color: 'border-blue-500/30', dotColor: 'bg-blue-500', textColor: 'text-blue-500', description: 'Reverse holo pattern' },
  { value: 'poke_ball', label: 'Poké Ball', color: 'border-red-500/30', dotColor: 'bg-red-500', textColor: 'text-red-500', description: 'Special Poké Ball pattern' },
  { value: 'master_ball', label: 'Master Ball', color: 'border-pink-500/30', dotColor: 'bg-pink-500', textColor: 'text-pink-500', description: 'Premium Master Ball pattern' }
];

const DYNAMIC_PALETTES = [
  { color: 'border-emerald-500/30', dotColor: 'bg-emerald-500', textColor: 'text-emerald-500' },
  { color: 'border-cyan-500/30', dotColor: 'bg-cyan-500', textColor: 'text-cyan-500' },
  { color: 'border-orange-500/30', dotColor: 'bg-orange-500', textColor: 'text-orange-500' },
  { color: 'border-teal-500/30', dotColor: 'bg-teal-500', textColor: 'text-teal-500' },
  { color: 'border-amber-500/30', dotColor: 'bg-amber-500', textColor: 'text-amber-500' },
  { color: 'border-violet-500/30', dotColor: 'bg-violet-500', textColor: 'text-violet-500' },
  { color: 'border-lime-500/30', dotColor: 'bg-lime-500', textColor: 'text-lime-500' },
  { color: 'border-rose-500/30', dotColor: 'bg-rose-500', textColor: 'text-rose-500' },
];

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function getFinishLabel(finish: string): string {
  const known: Record<string, string> = {
    normal: 'Normal',
    holofoil: 'Holofoil',
    reverse_holofoil: 'Reverse Holofoil',
    poke_ball: 'Poké Ball',
    master_ball: 'Master Ball',
    reverseHolofoil: 'Reverse Holofoil',
    energyReverseHolofoil: 'Energy Reverse Holo',
    pokeBallReverseHolofoil: 'Poké Ball Reverse Holo',
    masterBallReverseHolofoil: 'Master Ball Reverse Holo',
  };
  if (known[finish]) return known[finish];
  return finish
    .replace(/([A-Z])/g, ' $1')
    .replace(/([0-9]+)/g, ' $1 ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^./, s => s.toUpperCase())
    .trim();
}

export function getFinishOption(finish: string): FinishOption {
  const existing = ALL_FINISH_OPTIONS.find(o => o.value === finish);
  if (existing) return existing;
  const palette = DYNAMIC_PALETTES[hashStr(finish) % DYNAMIC_PALETTES.length];
  return {
    value: finish,
    label: getFinishLabel(finish),
    ...palette,
    description: getFinishLabel(finish),
  };
}

export function getFinishOptions(finishes: CardFinish[]): FinishOption[] {
  return finishes.map(getFinishOption);
}

export default function CollectionButton({ card, compact = false, defaultFinish = 'normal', availableFinishes }: CollectionButtonProps) {
  const { isAuthenticated } = useProfileStore();
  const { addCard, removeCard, getCardQuantity } = useCollectionStore();
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = async (finish: CardFinish, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      log('User not authenticated, redirecting to auth page');
      window.location.href = '/auth?register=true';
      return;
    }

    if (!card || !card.set?.id) {
      log('Invalid card data:', card);
      console.error('Invalid card data:', card);
      return;
    }

    setIsAdding(true);
    
    try {
      await addCard(card, 1, finish);
      log(`Added ${card.name} (${finish})`);
      
      const event = new CustomEvent('cardAdded', { 
        detail: { cardId: card.id }
      });
      document.dispatchEvent(event);
    } catch (error) {
      log(`Error adding card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (finish: CardFinish, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      return;
    }

    setIsAdding(true);
    
    try {
      await removeCard(card.id, finish, false); // Pass false to only remove one copy
      log(`Removed one copy of ${card.name} (${finish})`);
    } catch (error) {
      log(`Error removing card: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAdding(false);
    }
  };

  const visibleOptions = availableFinishes
    ? getFinishOptions(availableFinishes)
    : ALL_FINISH_OPTIONS;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleOptions.map((option) => {
        const quantity = getCardQuantity(card.id, option.value);
        
        return (
          <button
            key={option.value}
            onClick={(e) => handleAdd(option.value, e)}
            onContextMenu={(e) => handleRemove(option.value, e)}
            disabled={isAdding}
            className={`
              relative flex items-center justify-center w-6 h-6 rounded border transition-all
              ${option.color}
              ${quantity > 0 
                ? 'bg-white/20 shadow-lg' 
                : 'bg-white/5 hover:bg-white/10'
              }
              ${isAdding ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            title={`${option.label} (Left click to add, right click to remove)`}
          >
            <span className="text-xs font-medium">
              {quantity > 0 ? quantity : ''}
            </span>
          </button>
        );
      })}
    </div>
  );
}